import moment from "moment";
import { ClosedDay, ClosingRecurrence, ReservationPolicyModel, ScheduleData } from "src/api/reservation/reservation-type";
import { YmdFormat } from "src/utils/common-util";

// 자정 기준 분으로 표현한 하루 (0 ~ 1440)
const MINUTES_OF_DAY = 1440;

// 일자에 적용되는 휴무 구간 > 자정 기준 분 구간 + 휴무 사유
export type ClosedRange = {
  start: number;
  end: number;
  title?: string;
};

// 선택된 예약 정책의 휴무일 목록
// 유효 정책 조회는 공용공간 당 휴무일 정책 1건(기본 또는 개별)만 내려주므로 기본/개별 우선순위는 FE 에서 따지지 않는다
export const getClosingRecurrences = (policies?: ReservationPolicyModel[]): ClosingRecurrence[] =>
  policies?.find((policy) => policy.key.policyType === "closing-time-policy")?.details.closingTimePolicy?.recurrences || [];

// 해당 일자에 적용되는 휴무 구간 목록 > 휴무 기간 안에 있고 반복 요일이 일치하는 휴무일만 해당
export const getClosedRangesOfDate = (date: string, recurrences: ClosingRecurrence[]): ClosedRange[] => {
  const targetDate = moment(date).startOf("day");
  const isoDayOfWeek = targetDate.isoWeekday(); // 1=월 ~ 7=일

  return recurrences
    .filter(
      (recurrence) =>
        recurrence.daysOfWeek?.includes(isoDayOfWeek) &&
        targetDate.isSameOrAfter(moment(recurrence.effectiveFrom, "YYYY-MM-DD").startOf("day")) &&
        targetDate.isSameOrBefore(moment(recurrence.effectiveTo, "YYYY-MM-DD").startOf("day")),
    )
    .map((recurrence) => ({ start: recurrence.unavailableFrom, end: recurrence.unavailableUntil, title: recurrence.title }));
};

// slot 이 휴무 구간에 조금이라도 겹치면 해당 휴무 구간 반환 > 휴무 사유 노출에 사용 (taap closingRecurrence.ts와 같은 기준)
export const findClosedRangeOfSlot = (item: ScheduleData, ranges: ClosedRange[]) => {
  // 자정에 끝나는 마지막 slot 도 1440 으로 환산되도록 slot 시작일의 자정을 기준으로 계산한다
  const startOfSlotDay = moment(item.startDate).startOf("day");
  const slotStart = moment(item.startDate).diff(startOfSlotDay, "minutes");
  const slotEnd = moment(item.endDate).diff(startOfSlotDay, "minutes");

  return ranges.find((range) => range.start < slotEnd && slotStart < range.end);
};

// 하루 전체가 휴무인지 확인 > 휴무 구간이 나뉘어 있어도 이어 붙여서 24시간을 덮으면 휴무일로 본다
const isFullDayClosed = (ranges: ClosedRange[]) => {
  let closedUntil = 0;

  for (const range of [...ranges].sort((a, b) => a.start - b.start)) {
    if (range.start > closedUntil) return false;
    closedUntil = Math.max(closedUntil, range.end);
  }
  return closedUntil >= MINUTES_OF_DAY;
};

// 날짜 범위 중 하루 전체가 휴무인 날짜 목록 > 캘린더 휴무 표시에 사용
export const getClosedDays = (startDate: string, endDate: string, recurrences: ClosingRecurrence[]): ClosedDay[] => {
  if (recurrences.length === 0) return [];

  const closedDays: ClosedDay[] = [];
  const lastDate = moment(endDate).startOf("day");

  for (const date = moment(startDate).startOf("day"); date.isSameOrBefore(lastDate); date.add(1, "day")) {
    if (isFullDayClosed(getClosedRangesOfDate(date.format(YmdFormat.WITH_TIME_ZONE), recurrences))) {
      closedDays.push({ date: date.format(YmdFormat.WITH_TIME_ZONE), isClosed: true });
    }
  }
  return closedDays;
};
